import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Link, AlertTriangle, CheckCircle, XCircle, ExternalLink, FileText, RefreshCw, Download } from 'lucide-react';
import { useSEO } from '../hooks/useSEO';
import { toast } from 'react-hot-toast';
import AdContainer from '../components/ads/AdContainer';
import { GOOGLE_ADSENSE_CONFIG } from '../config/adsConfig';

interface BrokenLink {
  id: string;
  url: string;
  sourceUrl: string;
  linkText: string;
  statusCode: number;
  status: 'broken' | 'redirect' | 'working' | 'checking';
  responseTime: number;
  error?: string;
  redirectUrl?: string;
  linkType: 'internal' | 'external';
}

interface CrawlStats {
  totalLinks: number;
  checkedLinks: number;
  brokenLinks: number;
  redirects: number;
  workingLinks: number;
  averageResponseTime: number;
}

const BrokenLinkChecker: React.FC = () => {
  const [url, setUrl] = useState('');
  const [isChecking, setIsChecking] = useState(false);
  const [links, setLinks] = useState<BrokenLink[]>([]);
  const [stats, setStats] = useState<CrawlStats>({
    totalLinks: 0,
    checkedLinks: 0,
    brokenLinks: 0,
    redirects: 0,
    workingLinks: 0,
    averageResponseTime: 0
  });
  const [filter, setFilter] = useState<'all' | 'broken' | 'redirect' | 'working'>('all');
  const [depthLimit, setDepthLimit] = useState(2);
  const [checkExternal, setCheckExternal] = useState(true);

  useSEO({
    title: 'Broken Link Checker - Find Dead Links on Your Website',
    description: 'Scan your website for broken links, dead URLs, and redirects. Fix 404 errors and improve SEO with comprehensive link analysis.',
    keywords: 'broken link checker, dead link finder, 404 error checker, link validator, website crawler, SEO audit',
    canonical: 'https://snap-tools.online/seo/broken-link-checker'
  });

  // Check links on website
  const checkWebsiteLinks = async () => {
    if (!url) {
      toast.error('Please enter a URL to check');
      return;
    }

    // Validate URL
    let baseUrl = url;
    try {
      if (!url.startsWith('http://') && !url.startsWith('https://')) {
        baseUrl = 'https://' + url;
      }
      new URL(baseUrl);
    } catch (error) {
      toast.error('Please enter a valid URL');
      return;
    }

    setIsChecking(true);
    setLinks([]);
    setStats({
      totalLinks: 0,
      checkedLinks: 0,
      brokenLinks: 0,
      redirects: 0,
      workingLinks: 0,
      averageResponseTime: 0
    });

    try {
      // In a real implementation, you would crawl the website and check links
      // For demo purposes, we'll simulate the link checking process
      
      // Generate mock links
      const mockLinks: Omit<BrokenLink, 'id'>[] = [
        {
          url: `${baseUrl}/about`,
          sourceUrl: baseUrl,
          linkText: 'About Us',
          statusCode: 200,
          status: 'working',
          responseTime: 150,
          linkType: 'internal'
        },
        {
          url: `${baseUrl}/contact`,
          sourceUrl: baseUrl,
          linkText: 'Contact',
          statusCode: 404,
          status: 'broken',
          responseTime: 200,
          error: 'Page not found',
          linkType: 'internal'
        },
        {
          url: `${baseUrl}/products`,
          sourceUrl: baseUrl,
          linkText: 'Products',
          statusCode: 301,
          status: 'redirect',
          responseTime: 180,
          redirectUrl: `${baseUrl}/shop`,
          linkType: 'internal'
        },
        {
          url: 'https://example.com/missing-page',
          sourceUrl: baseUrl,
          linkText: 'External Resource',
          statusCode: 404,
          status: 'broken',
          responseTime: 300,
          error: 'External page not found',
          linkType: 'external'
        },
        {
          url: 'https://github.com',
          sourceUrl: baseUrl,
          linkText: 'GitHub',
          statusCode: 200,
          status: 'working',
          responseTime: 120,
          linkType: 'external'
        },
        {
          url: `${baseUrl}/old-blog`,
          sourceUrl: `${baseUrl}/blog`,
          linkText: 'Legacy Blog',
          statusCode: 500,
          status: 'broken',
          responseTime: 250,
          error: 'Internal server error',
          linkType: 'internal'
        }
      ];

      // Add more random links
      for (let i = 0; i < 15; i++) {
        const isWorking = Math.random() > 0.3; // 70% working links
        const isRedirect = !isWorking && Math.random() > 0.6; // Some redirects
        const isInternal = Math.random() > 0.3; // 70% internal links

        mockLinks.push({
          url: isInternal ? `${baseUrl}/page-${i}` : `https://external-site-${i}.com`,
          sourceUrl: baseUrl,
          linkText: `Link ${i + 1}`,
          statusCode: isWorking ? 200 : (isRedirect ? 301 : 404),
          status: isWorking ? 'working' : (isRedirect ? 'redirect' : 'broken'),
          responseTime: Math.floor(Math.random() * 400) + 100,
          error: !isWorking && !isRedirect ? 'Page not found' : undefined,
          redirectUrl: isRedirect ? `${baseUrl}/new-page-${i}` : undefined,
          linkType: isInternal ? 'internal' : 'external'
        });
      }

      // Filter external links if not checking them
      const filteredLinks = checkExternal ? mockLinks : mockLinks.filter(link => link.linkType === 'internal');

      // Simulate checking process
      for (let i = 0; i < filteredLinks.length; i++) {
        const link = filteredLinks[i];
        const linkWithId: BrokenLink = {
          ...link,
          id: Math.random().toString(36).substr(2, 9),
          status: 'checking'
        };

        setLinks(prev => [...prev, linkWithId]);

        // Simulate delay
        await new Promise(resolve => setTimeout(resolve, 200));

        // Update with final status
        setLinks(prev => prev.map(l => 
          l.id === linkWithId.id 
            ? { ...linkWithId, status: link.status as any }
            : l
        ));

        // Update stats
        setStats(prev => {
          const checkedLinks = i + 1;
          const brokenLinks = prev.brokenLinks + (link.status === 'broken' ? 1 : 0);
          const redirects = prev.redirects + (link.status === 'redirect' ? 1 : 0);
          const workingLinks = prev.workingLinks + (link.status === 'working' ? 1 : 0);
          const totalResponseTime = prev.averageResponseTime * prev.checkedLinks + link.responseTime;
          const averageResponseTime = totalResponseTime / checkedLinks;

          return {
            totalLinks: filteredLinks.length,
            checkedLinks,
            brokenLinks,
            redirects,
            workingLinks,
            averageResponseTime
          };
        });
      }

      toast.success(`Scan completed! Found ${filteredLinks.filter(l => l.status === 'broken').length} broken links`);
    } catch (error) {
      console.error('Link check failed:', error);
      toast.error('Failed to check links');
    } finally {
      setIsChecking(false);
    }
  };

  // Load example
  const loadExample = () => {
    setUrl('https://example.com');
    toast.success('Example URL loaded');
  };

  // Filter links
  const filteredLinks = links.filter(link => {
    if (filter === 'all') return true;
    return link.status === filter;
  });

  // Export results
  const exportResults = () => {
    const csvContent = [
      'URL,Source URL,Link Text,Status Code,Status,Response Time,Error,Redirect URL,Link Type',
      ...links.map(link => [
        link.url,
        link.sourceUrl,
        link.linkText,
        link.statusCode,
        link.status,
        link.responseTime,
        link.error || '',
        link.redirectUrl || '',
        link.linkType
      ].join(','))
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'broken-links-report.csv';
    a.click();
    URL.revokeObjectURL(url);
    toast.success('Report exported successfully');
  };

  // Get status icon
  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'working': return <CheckCircle className="w-5 h-5 text-green-600" />;
      case 'broken': return <XCircle className="w-5 h-5 text-red-600" />;
      case 'redirect': return <ExternalLink className="w-5 h-5 text-yellow-600" />;
      case 'checking': return <RefreshCw className="w-5 h-5 text-blue-600 animate-spin" />;
      default: return <AlertTriangle className="w-5 h-5 text-gray-600" />;
    }
  };

  // Get status color
  const getStatusColor = (status: string) => {
    switch (status) {
      case 'working': return 'text-green-600';
      case 'broken': return 'text-red-600';
      case 'redirect': return 'text-yellow-600';
      case 'checking': return 'text-blue-600';
      default: return 'text-gray-600';
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-50 to-indigo-100 py-8">
      <div className="container mx-auto px-4 max-w-6xl">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="bg-white rounded-2xl shadow-xl p-6 md:p-8"
        >
          <div className="text-center mb-8">
            <div className="inline-flex items-center justify-center w-16 h-16 bg-purple-100 rounded-full mb-4">
              <Link className="w-8 h-8 text-purple-600" />
            </div>
            <h1 className="text-3xl font-bold text-gray-900 mb-2">Broken Link Checker</h1>
            <p className="text-gray-600 max-w-2xl mx-auto">
              Scan your website for broken links, dead URLs, and redirects. Identify and fix 404 errors to improve SEO and user experience.
            </p>
          </div>

          <AdContainer 
            position="header"
            adSlot={GOOGLE_ADSENSE_CONFIG.adSlots.imageConverter.header}
            className="mb-6"
          />

          {/* Configuration */}
          <div className="bg-gray-50 rounded-lg p-6 mb-8">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Website Analysis Settings</h3>
            <div className="space-y-4">
              <div className="flex gap-3">
                <input
                  type="url"
                  value={url}
                  onChange={(e) => setUrl(e.target.value)}
                  placeholder="https://example.com"
                  className="flex-1 p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                  onKeyPress={(e) => e.key === 'Enter' && checkWebsiteLinks()}
                />
                <button
                  onClick={loadExample}
                  className="bg-blue-600 text-white px-6 py-3 rounded-lg hover:bg-blue-700 transition-colors"
                >
                  Example
                </button>
              </div>

              <div className="grid md:grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Crawl Depth
                  </label>
                  <select
                    value={depthLimit}
                    onChange={(e) => setDepthLimit(parseInt(e.target.value))}
                    className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                  >
                    <option value={1}>1 level (homepage only)</option>
                    <option value={2}>2 levels</option>
                    <option value={3}>3 levels</option>
                    <option value={5}>5 levels</option>
                  </select>
                </div>

                <div className="flex items-center">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={checkExternal}
                      onChange={(e) => setCheckExternal(e.target.checked)}
                      className="rounded border-gray-300 text-purple-600 focus:ring-purple-500"
                    />
                    <span className="text-sm font-medium text-gray-700">
                      Check External Links
                    </span>
                  </label>
                </div>

                <div className="flex items-end">
                  <button
                    onClick={checkWebsiteLinks}
                    disabled={isChecking || !url}
                    className="w-full flex items-center justify-center gap-2 bg-purple-600 text-white px-6 py-3 rounded-lg hover:bg-purple-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                  >
                    {isChecking ? (
                      <RefreshCw className="w-4 h-4 animate-spin" />
                    ) : (
                      <Link className="w-4 h-4" />
                    )}
                    {isChecking ? 'Checking...' : 'Start Scan'}
                  </button>
                </div>
              </div>
            </div>
          </div>

          {/* Statistics */}
          {stats.totalLinks > 0 && (
            <div className="bg-white border border-gray-200 rounded-lg p-6 mb-8">
              <div className="flex items-center justify-between mb-6">
                <h3 className="text-lg font-semibold text-gray-900">Scan Results</h3>
                <div className="flex gap-3">
                  <button
                    onClick={exportResults}
                    disabled={links.length === 0}
                    className="flex items-center gap-2 border border-gray-300 text-gray-700 px-4 py-2 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                  >
                    <Download className="w-4 h-4" />
                    Export CSV
                  </button>
                </div>
              </div>

              <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-6">
                <div className="bg-blue-50 rounded-lg p-4">
                  <div className="text-2xl font-bold text-blue-600">{stats.totalLinks}</div>
                  <div className="text-sm text-blue-700">Total Links</div>
                </div>
                <div className="bg-green-50 rounded-lg p-4">
                  <div className="text-2xl font-bold text-green-600">{stats.workingLinks}</div>
                  <div className="text-sm text-green-700">Working</div>
                </div>
                <div className="bg-red-50 rounded-lg p-4">
                  <div className="text-2xl font-bold text-red-600">{stats.brokenLinks}</div>
                  <div className="text-sm text-red-700">Broken</div>
                </div>
                <div className="bg-yellow-50 rounded-lg p-4">
                  <div className="text-2xl font-bold text-yellow-600">{stats.redirects}</div>
                  <div className="text-sm text-yellow-700">Redirects</div>
                </div>
                <div className="bg-purple-50 rounded-lg p-4">
                  <div className="text-2xl font-bold text-purple-600">
                    {Math.round(stats.averageResponseTime)}ms
                  </div>
                  <div className="text-sm text-purple-700">Avg Response</div>
                </div>
              </div>

              {/* Progress Bar */}
              <div className="bg-gray-200 rounded-full h-2 mb-4">
                <div 
                  className="bg-purple-600 h-2 rounded-full transition-all duration-300"
                  style={{ width: `${(stats.checkedLinks / stats.totalLinks) * 100}%` }}
                />
              </div>
              <div className="text-sm text-gray-600 text-center">
                Progress: {stats.checkedLinks} / {stats.totalLinks} links checked
              </div>
            </div>
          )}

          {/* Filter and Results */}
          {links.length > 0 && (
            <div className="bg-white border border-gray-200 rounded-lg p-6 mb-8">
              <div className="flex items-center justify-between mb-6">
                <h3 className="text-lg font-semibold text-gray-900">Link Details</h3>
                
                <div className="flex bg-gray-100 rounded-lg p-1">
                  {[
                    { id: 'all', label: 'All', count: links.length },
                    { id: 'broken', label: 'Broken', count: links.filter(l => l.status === 'broken').length },
                    { id: 'redirect', label: 'Redirects', count: links.filter(l => l.status === 'redirect').length },
                    { id: 'working', label: 'Working', count: links.filter(l => l.status === 'working').length }
                  ].map((filterOption) => (
                    <button
                      key={filterOption.id}
                      onClick={() => setFilter(filterOption.id as any)}
                      className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
                        filter === filterOption.id
                          ? 'bg-white text-purple-600 shadow-sm'
                          : 'text-gray-600 hover:text-gray-900'
                      }`}
                    >
                      {filterOption.label} ({filterOption.count})
                    </button>
                  ))}
                </div>
              </div>

              <div className="space-y-3 max-h-96 overflow-y-auto">
                {filteredLinks.map((link) => (
                  <div key={link.id} className="border border-gray-200 rounded-lg p-4">
                    <div className="flex items-start justify-between mb-2">
                      <div className="flex items-center gap-3 flex-1">
                        {getStatusIcon(link.status)}
                        <div className="min-w-0 flex-1">
                          <div className="font-medium text-gray-900 truncate">
                            {link.linkText || 'Untitled Link'}
                          </div>
                          <div className="text-sm text-blue-600 hover:underline truncate">
                            <a href={link.url} target="_blank" rel="noopener noreferrer">
                              {link.url}
                            </a>
                          </div>
                          <div className="text-xs text-gray-500">
                            Found on: {link.sourceUrl}
                          </div>
                        </div>
                      </div>

                      <div className="text-right ml-4">
                        <div className={`text-sm font-semibold capitalize ${getStatusColor(link.status)}`}>
                          {link.status}
                        </div>
                        {link.status !== 'checking' && (
                          <>
                            <div className="text-xs text-gray-500">
                              {link.statusCode} • {link.responseTime}ms
                            </div>
                            <div className={`text-xs px-2 py-1 rounded-full mt-1 ${
                              link.linkType === 'internal' 
                                ? 'bg-blue-100 text-blue-800' 
                                : 'bg-gray-100 text-gray-800'
                            }`}>
                              {link.linkType}
                            </div>
                          </>
                        )}
                      </div>
                    </div>

                    {link.error && (
                      <div className="mt-2 text-sm text-red-600 bg-red-50 rounded p-2">
                        {link.error}
                      </div>
                    )}

                    {link.redirectUrl && (
                      <div className="mt-2 text-sm text-yellow-700 bg-yellow-50 rounded p-2">
                        Redirects to: {link.redirectUrl}
                      </div>
                    )}
                  </div>
                ))}

                {filteredLinks.length === 0 && (
                  <div className="text-center py-8 text-gray-500">
                    <FileText className="w-12 h-12 mx-auto mb-4 text-gray-400" />
                    <p>No links match the current filter</p>
                  </div>
                )}
              </div>
            </div>
          )}

          <AdContainer 
            position="footer"
            adSlot={GOOGLE_ADSENSE_CONFIG.adSlots.imageConverter.footer}
            className="mb-6"
          />

          {/* Educational Content */}
          <div className="grid md:grid-cols-2 gap-6">
            <div className="bg-red-50 rounded-lg p-6">
              <h4 className="font-semibold text-red-900 mb-3">Why Fix Broken Links?</h4>
              <ul className="text-sm text-red-800 space-y-2">
                <li>• <strong>SEO Impact:</strong> Broken links hurt search engine rankings</li>
                <li>• <strong>User Experience:</strong> 404 pages frustrate visitors</li>
                <li>• <strong>Crawl Budget:</strong> Search engines waste time on broken links</li>
                <li>• <strong>Link Equity:</strong> Broken internal links lose PageRank flow</li>
                <li>• <strong>Trust:</strong> Broken links make sites appear outdated</li>
                <li>• <strong>Conversion:</strong> Broken checkout/contact links lose business</li>
              </ul>
            </div>

            <div className="bg-green-50 rounded-lg p-6">
              <h4 className="font-semibold text-green-900 mb-3">How to Fix Broken Links</h4>
              <ul className="text-sm text-green-800 space-y-2">
                <li>• <strong>404s:</strong> Fix URLs, create content, or remove links</li>
                <li>• <strong>Redirects:</strong> Update links to final destinations</li>
                <li>• <strong>External Links:</strong> Find alternative sources or remove</li>
                <li>• <strong>Images:</strong> Upload missing images or update paths</li>
                <li>• <strong>Regular Checks:</strong> Monitor links monthly</li>
                <li>• <strong>301 Redirects:</strong> Set up permanent redirects for moved pages</li>
              </ul>
            </div>

            <div className="bg-blue-50 rounded-lg p-6">
              <h4 className="font-semibold text-blue-900 mb-3">Link Checking Best Practices</h4>
              <ul className="text-sm text-blue-800 space-y-2">
                <li>• <strong>Regular Audits:</strong> Check links monthly or quarterly</li>
                <li>• <strong>Automated Monitoring:</strong> Use tools for continuous checking</li>
                <li>• <strong>Prioritize Internal:</strong> Fix internal links first</li>
                <li>• <strong>Check After Updates:</strong> Verify links after site changes</li>
                <li>• <strong>Monitor External:</strong> Track outbound link health</li>
                <li>• <strong>Document Fixes:</strong> Keep track of resolved issues</li>
              </ul>
            </div>

            <div className="bg-yellow-50 rounded-lg p-6">
              <h4 className="font-semibold text-yellow-900 mb-3">HTTP Status Codes</h4>
              <ul className="text-sm text-yellow-800 space-y-2">
                <li>• <strong>200:</strong> OK - Link works perfectly</li>
                <li>• <strong>301:</strong> Moved Permanently - Update the link</li>
                <li>• <strong>302:</strong> Found - Temporary redirect</li>
                <li>• <strong>404:</strong> Not Found - Page doesn't exist</li>
                <li>• <strong>500:</strong> Server Error - Temporary server issue</li>
                <li>• <strong>503:</strong> Service Unavailable - Server overloaded</li>
              </ul>
            </div>
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default BrokenLinkChecker;
